import CancelIcon from '@mui/icons-material/Cancel';
import CloseIcon from '@mui/icons-material/Close';
import { Autocomplete, TextField, IconButton } from '@mui/material';
import { styled } from '@mui/system';
import { equals } from 'ramda';
import * as React from 'react';
import { useIntl } from 'react-intl';
import { isNil } from 'Utility/ramdaReplacement';
import { capitalize } from 'Utility/string';
import colors from '../../../config/theme/colors';
import { field, historyValue } from '../../../config/theme/componentTheme';

const StyledDiv = styled('div', {
  shouldForwardProp: prop => prop !== 'width',
})(({ width }: { width?: number }) => ({
  maxWidth: `${width ? width + 'rem' : field.width}`,
}));

const StyledListItem = styled('div')({
  display: 'flex',
  padding: '0rem 0rem 0rem 1.4rem',
  border: `1px solid ${colors.mediumGray}`,
  borderRadius: '1.5rem',
  color: colors.darkGray,
  fontWeight: 600,
  fontSize: '1.4rem',
  alignItems: 'center',
  justifyContent: 'space-between',
  height: '3rem',
});

const StyledListTitle = styled('div')({
  paddingBottom: '0.22rem',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  minWidth: 0,
  flex: 1,
});

const StyledListIcon = styled('div')({
  fontSize: '2rem',
  color: colors.mediumGray,
  height: '3rem',
  flexShrink: 0,
  '&:hover': {
    color: `${colors.mediumGray}90`,
    cursor: 'pointer',
  },
});

const textElements = (
  groupID: string,
  value?: Array<{ [key: string]: string }>,
  labelID?: string,
  renderOption?: (o: any) => React.ReactNode,
  optionFormatter?: (o: { [key: string]: string }) => React.JSX.Element | string,
): React.JSX.Element => (
  <div>
    {Array.isArray(value) && value.length > 0
      ? value.map((v, i) => (
          <div key={i}>
            {optionFormatter
              ? optionFormatter(v)
              : renderOption
                ? renderOption(v)
                : labelID && !isNil(v[labelID]) && !isNil(v[groupID])
                  ? `${capitalize(v[groupID])} - ${capitalize(v[labelID])}`
                  : labelID && !isNil(v[labelID])
                    ? capitalize(v[labelID])
                    : ''}
          </div>
        ))
      : '-'}
  </div>
);

const SelectElements = ({
  name,
  value = [],
  options,
  onChange,
  groupID,
  labelID,
  getOptionLabel,
  renderOption,
  groupBy,
  renderGroup,
  onInputChange,
  placeholder,
  width,
  disabled,
  limit,
  tableRowContent,
  optionFormatter,
}: IInputBasics & IAutoCompleteSelectAndAdd): React.JSX.Element => {
  const [resetKey, setResetKey] = React.useState(0);
  const { formatMessage } = useIntl();
  const fm = (id: string) => formatMessage({ id });

  const optionsFiltered = options.filter(opt => !value.some(v => equals(v, opt)));

  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  const isDisabled = disabled || optionsFiltered.length === 0 || (typeof limit === 'number' && value.length >= limit);

  const handleAdd = (_e: React.SyntheticEvent, selected: any): void => {
    if (!selected) return;
    onChange && onChange({ [name]: [...value, selected] });
    setResetKey(k => k + 1);
  };

  const handleDelete = (index: number): void => {
    const newValue = value.slice();
    newValue.splice(index, 1);
    onChange && onChange({ [name]: newValue });
  };

  const resolvedGetOptionLabel =
    getOptionLabel ??
    ((option: any): string => (labelID && !isNil(option[labelID]) ? capitalize(option[labelID]) : ''));

  const resolvedGroupBy = (option: any): string =>
    !isNil(option[groupID]) && groupBy ? groupBy(option) : !isNil(option[groupID]) ? capitalize(option[groupID]) : '';

  const getItemLabel = (v: { [key: string]: string }): string =>
    optionFormatter ? String(optionFormatter(v)) : resolvedGetOptionLabel(v);

  return (
    <React.Fragment>
      {!tableRowContent && Array.isArray(value) && value.length > 0 && (
        <StyledDiv width={width} style={{ marginBottom: '1rem' }}>
          {value.map((v, i) => (
            <StyledListItem key={i} style={{ marginBottom: i === value.length - 1 ? undefined : '1rem' }}>
              <StyledListTitle>{optionFormatter ? optionFormatter(v) : resolvedGetOptionLabel(v)}</StyledListTitle>
              {!disabled && (
                <StyledListIcon onClick={() => handleDelete(i)}>
                  <CancelIcon style={{ width: '3rem', height: '3rem' }} />
                </StyledListIcon>
              )}
            </StyledListItem>
          ))}
        </StyledDiv>
      )}
      <Autocomplete
        key={resetKey}
        value={null}
        onInputChange={onInputChange}
        options={optionsFiltered}
        noOptionsText={fm('general.noOptions')}
        disabled={isDisabled}
        getOptionLabel={resolvedGetOptionLabel}
        isOptionEqualToValue={(opt, val) => equals(opt, val)}
        groupBy={resolvedGroupBy}
        renderGroup={renderGroup}
        renderInput={params => (
          <TextField {...params} placeholder={typeof placeholder === 'string' ? placeholder : ''} />
        )}
        renderOption={(
          { key, ...p }: React.HTMLAttributes<HTMLLIElement> & { key: React.Key },
          o: { [key: string]: string },
        ) => (
          <li key={key} {...p}>
            {renderOption ? renderOption(o) : resolvedGetOptionLabel(o)}
          </li>
        )}
        onChange={handleAdd}
        clearText={fm('general.clear')}
        style={{ width: `${width ?? 20}rem` }}
      />
      {tableRowContent && (
        <table style={{ borderCollapse: 'collapse', marginTop: '1rem' }}>
          <tbody>
            {Array.isArray(value) &&
              value.length > 0 &&
              value.map((val, i) => (
                <tr key={i} style={{ backgroundColor: i % 2 === 0 ? undefined : colors.lightestGray }}>
                  <td style={{ color: colors.tertiaryText, padding: 5, minWidth: '25rem' }}>{getItemLabel(val)}</td>
                  {tableRowContent(val).map((cellContent, j) => (
                    <td key={j}>{cellContent}</td>
                  ))}
                  <td>
                    {!disabled && (
                      <IconButton size="small" sx={{ color: colors.mediumGray }} onClick={() => handleDelete(i)}>
                        <CloseIcon />
                      </IconButton>
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      )}
    </React.Fragment>
  );
};

const AutoCompleteSelectAndAdd = ({
  editing = false,
  value,
  groupID,
  labelID,
  renderOption,
  optionFormatter,
  width,
  ...props
}: IInputBasics & IAutoCompleteSelectAndAdd): React.JSX.Element =>
  !editing ? (
    <div style={historyValue}>{textElements(groupID, value, labelID, renderOption, optionFormatter)}</div>
  ) : (
    <SelectElements
      groupID={groupID}
      value={Array.isArray(value) ? value : []}
      labelID={labelID}
      renderOption={renderOption}
      optionFormatter={optionFormatter}
      width={width}
      {...props}
    />
  );

export default AutoCompleteSelectAndAdd;
